import {
  Stack,
  Grid,
  Title,
  Text,
  Divider,
  Flex,
  Box,
  ActionIcon,
} from '@mantine/core';
import { EditorJsRender } from '@organisms/EditorJsRender';
import { FC } from 'react';
import { NewsDetailsProps } from './types';
import { NEWS_HEADER_IMAGE } from 'src/mockdata';
import AuthorsCard from '@molecules/frontoffice/AuthorsCard';
import { getFormattedDate } from '@utils/dates';
import { RandomPublicityComponent } from '@organisms/RandomPublicityComponent';
import { ShareIcon } from '@heroicons/react/24/solid';
import Image from 'next/image';
import Link from 'next/link';
import { Badge } from '@atoms/Badge';
import { NewsIframe } from '../NewsIframe';
import { NewspaperLastNews } from '@molecules/frontoffice/NewspaperLastNews';
import { TitleUnderline } from '@atoms/TitleUnderline';
import { useTranslation } from 'next-i18next';

export const NewsDetails: FC<NewsDetailsProps> = ({
  description,
  title,
  author,
  subTitle,
  headerImage,
  date,
  episode,
  publicities,
  handleShareNews,
  category,
  lastNews,
  isLastNewsLoading,
}) => {
  const { t } = useTranslation('common');
  const hasSidebarPublicity =
    publicities && publicities.some((pub) => pub.type === 'SIDEBAR');
  const existsNewsPub =
    publicities && publicities.some((pub) => pub.type === 'NEWS');

  return (
    <>
      <Grid gutter={40}>
        <Grid.Col sm={10} md={12}>
          <Stack spacing="xs">
            <Link
              href={`/noticias/${category?.slug}`}
              style={{ textDecoration: 'none', width: '100%' }}
              aria-label={`link-${category?.name}`}
            >
              <Badge variant="filled" radius="none" color={category?.color}>
                {category?.name}
              </Badge>
            </Link>
            <Title mt={8} order={1} variant="h1">
              {title}
            </Title>
            <Text variant="body2" component="p">
              {subTitle}
            </Text>
          </Stack>
        </Grid.Col>
      </Grid>
      <Grid mt={2} gutter={40}>
        <Grid.Col pos="relative" sm={12} md={12} order={1} orderSm={2}>
          <Image
            src={headerImage ?? NEWS_HEADER_IMAGE}
            priority
            height={768}
            width={1366}
            sizes="(max-width: 768px) 900px, 1366px"
            alt={title}
            style={{ width: '100%', height: 'auto', maxHeight: 768 }}
          />
        </Grid.Col>
      </Grid>
      <Grid mt={10} gutter={40}>
        <Grid.Col sm={0} md={3} orderSm={2}>
          <Flex direction="column" w="100%" h="100%">
            <Stack
              mb={hasSidebarPublicity || existsNewsPub ? 32 : 0}
              spacing="xs"
              w="100%"
              h="fit-content"
              mt={{ base: 0, md: episode ? 0 : 12 }}
            >
              <Divider />
              <AuthorsCard size={40} author={author} padding={0} />
              <Divider />
              <Flex justify="space-between" align="center">
                <Text variant="caption2" component="span">
                  {getFormattedDate(date)}
                </Text>
                <ActionIcon
                  id="share-live-blog-button"
                  aria-label="share-live-blog-button"
                  onClick={handleShareNews}
                >
                  <ShareIcon width={16} />
                </ActionIcon>
              </Flex>
              <Divider />
            </Stack>
            {hasSidebarPublicity && (
              <Box display={{ base: 'none', md: 'block' }} w="100%">
                <RandomPublicityComponent
                  index={0}
                  type="SIDEBAR"
                  publicities={publicities}
                />
              </Box>
            )}
            <Box
              sx={{
                position: 'sticky',
                alignSelf: 'flex-start',
                top: 0,
              }}
              w="100%"
              pt={{ base: 0, md: 70 }}
            >
              {existsNewsPub && (
                <RandomPublicityComponent
                  index={0}
                  type="NEWS"
                  publicities={publicities}
                />
              )}
              {lastNews && (
                <Box
                  mt={existsNewsPub ? 64 : 0}
                  display={{ base: 'none', md: 'block' }}
                >
                  <Link
                    href="/ultimas"
                    style={{
                      textDecoration: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      marginBottom: 20,
                    }}
                  >
                    <Title variant="h3Orange" mb={0}>
                      {t('common.lastOnes')}
                    </Title>
                    <TitleUnderline />
                  </Link>
                  <NewspaperLastNews
                    news={lastNews}
                    isLoading={isLastNewsLoading ?? false}
                    showTitle={false}
                    showFooter
                    size="small"
                  />
                </Box>
              )}
            </Box>
          </Flex>
        </Grid.Col>
        <Grid.Col
          sm={12}
          md={9}
          order={1}
          orderSm={1}
          pr={{ base: 20, md: 120 }}
        >
          {episode && <NewsIframe episode={episode} />}
          <EditorJsRender data={description} linkColor="currentColor" />
        </Grid.Col>
      </Grid>
      {lastNews && (
        <Box display={{ base: 'block', md: 'none' }} mt={80}>
          <Link
            href="/ultimas"
            style={{
              textDecoration: 'none',
              display: 'flex',
              flexDirection: 'column',
              marginBottom: 20,
            }}
          >
            <Title variant="h3Orange" mb={0}>
              {t('common.lastOnes')}
            </Title>
            <TitleUnderline />
          </Link>
          <NewspaperLastNews
            news={lastNews}
            isLoading={isLastNewsLoading ?? false}
            showTitle={false}
            showFooter
            size="small"
          />
        </Box>
      )}
    </>
  );
};
